import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { updateManagementTask } from '../../services/api/implementation/impl';
import { toDateOnly } from './taskWorkflow';
import {
    AUTHORITY_REQUIRED_MESSAGE,
    APPLICATION_NUMBER_REQUIRED_MESSAGE,
    PERMISSION_END_BEFORE_START_MESSAGE,
    kindLabel,
    missingPermissionAuthority,
    missingPermissionApplicationNumber,
    permissionEndBeforeStart
} from './permission';

// Owner-only on the API; sends just the permission-flow fields, the task's kind is kept.
const EditPermissionModal = ({ task, onClose }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit } = useForm({
        defaultValues: {
            authority: task?.authority || '',
            applicationNumber: task?.applicationNumber || '',
            permissionStartDate: toDateOnly(task?.permissionStartDate),
            permissionEndDate: toDateOnly(task?.permissionEndDate)
        }
    });

    const updateMutation = useMutation({
        mutationFn: (payload) => updateManagementTask(task.id, payload),
        onSuccess: () => {
            toast.success(`${kindLabel(task?.kind)} details updated`);
            queryClient.invalidateQueries(['managementTask']);
            onClose();
        },
        onError: (error) => {
            toast.error(
                error?.response?.data?.message ||
                    `Failed to update ${kindLabel(task?.kind).toLowerCase()} details`
            );
        }
    });

    const onSubmit = (data) => {
        if (missingPermissionAuthority(data.authority)) {
            toast.error(AUTHORITY_REQUIRED_MESSAGE);
            return;
        }
        if (missingPermissionApplicationNumber(data.applicationNumber)) {
            toast.error(APPLICATION_NUMBER_REQUIRED_MESSAGE);
            return;
        }
        if (permissionEndBeforeStart(data.permissionStartDate, data.permissionEndDate)) {
            toast.error(PERMISSION_END_BEFORE_START_MESSAGE);
            return;
        }
        updateMutation.mutate({
            authority: data.authority.trim(),
            applicationNumber: data.applicationNumber.trim(),
            permissionStartDate: data.permissionStartDate || null,
            permissionEndDate: data.permissionEndDate || null
        });
    };

    return (
        <Modal
            name={`Edit ${kindLabel(task?.kind)} Details`}
            width={50}
            onClose={onClose}
            hideClose={false}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <FormInput
                        label="Authority"
                        register={register}
                        register_key="authority"
                        type="text"
                        isRequired={true}
                        width={100}
                    />
                    <FormInput
                        label="Application no."
                        register={register}
                        register_key="applicationNumber"
                        type="text"
                        isRequired={true}
                        width={100}
                    />
                    <FormInput
                        label="Start Date"
                        register={register}
                        register_key="permissionStartDate"
                        type="date"
                        width={100}
                    />
                    <FormInput
                        label="End Date"
                        register={register}
                        register_key="permissionEndDate"
                        type="date"
                        width={100}
                    />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={updateMutation.isPending}
                        className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e] disabled:opacity-50"
                    >
                        {updateMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default EditPermissionModal;
